import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { endpoints } from 'src/environments/endpoints';
import { Beneficiario } from '../../models/beneficiario/Beneficiario';
import { BloquearProdutoBeneficiarioPayload } from '../../models/beneficiario/BloquearProdutoBeneficiarioPayload';
import { CreateProdutoBeneficiarioPayload } from '../../models/beneficiario/CreateProdutoBeneficiarioPayload';
import { DocumentoBeneficiario } from '../../models/beneficiario/DocumentoBeneficiario';
import { EditBeneficiarioPayload } from '../../models/beneficiario/EditBeneficiarioPayload';
import { ListBeneficiariosResponse } from '../../models/beneficiario/ListBeneficiariosResponse';
import { ProdutoBeneficiario } from '../../models/beneficiario/ProdutoBeneficiario';
import { ProdutoComPrecoDoBeneficiario } from '../../models/conferencia-de-fatura/ProdutoComPrecoDoBeneficiario';
import { SaveBeneficiarioPayload } from '../../models/beneficiario/SaveBeneficiarioPayload';
import { TipoBeneficiario } from '../../models/beneficiario/TipoBeneficiario';
import { ProdutoBeneficiarioPainelAtendimento } from '../../models/painel-de-atendimento/ProdutoBeneficiarioPainelAtendimento';
import { isNullOrWhiteSpace } from '../../utils/isNullOrWhiteSpace';

@Injectable({
  providedIn: 'root',
})
export class BeneficiarioService {
  http = inject(HttpClient);

  findBeneficiario(guidBeneficiario: string): Observable<Beneficiario> {
    const baseUrl = endpoints.beneficiario;
    const url = `${baseUrl}/${guidBeneficiario}`;

    return this.http.get<Beneficiario>(url);
  }

  editBeneficiario(arg: EditBeneficiarioPayload): Observable<void> {
    const baseUrl = endpoints.beneficiario;
    const { guidBeneficiario, guidTitular, ...payload } = arg;
    const url = `${baseUrl}/${guidBeneficiario}`;

    const headers = new HttpHeaders({ guidTitular });

    return this.http.put<void>(url, payload, { headers });
  }

  createBeneficiario(payload: SaveBeneficiarioPayload): Observable<void> {
    const baseUrl = endpoints.beneficiario;
    const { guidTitular, ..._payload } = payload;

    let headers = new HttpHeaders();

    if (guidTitular) {
      headers = headers.set('guidTitular', guidTitular);
    }
    return this.http.post<void>(baseUrl, _payload, { headers });
  }

  getAllBeneficiarios(requestParams?: {
    pageIndex?: number;
    pageSize?: number;
    tipoFiltro?: string;
    valorFiltro?: string;
    guidTitular?: string;
  }): Observable<ListBeneficiariosResponse> {
    const baseUrl = endpoints.beneficiario;
    const url = `${baseUrl}/listar`;

    let params = new HttpParams();

    if (requestParams?.pageIndex != null) {
      params = params.set('pageIndex', requestParams.pageIndex);
    }

    if (requestParams?.pageSize != null) {
      params = params.set('pageSize', requestParams.pageSize);
    }

    if (requestParams?.tipoFiltro != null) {
      params = params.set(
        requestParams.tipoFiltro,
        requestParams.valorFiltro ?? ''
      );
    }

    if (requestParams?.guidTitular != null) {
      params = params.set('guidTitular', requestParams.guidTitular);
    }

    return this.http.get<ListBeneficiariosResponse>(url, {
      params,
    });
  }

  getProdutosBeneficiario(
    guidBeneficiario: string
  ): Observable<ProdutoBeneficiario[]> {
    const baseUrl = endpoints.produtoBeneficiario;
    const url = `${baseUrl}/${guidBeneficiario}`;

    return this.http.get<ProdutoBeneficiario[]>(url);
  }

  // #20767: produtos do beneficiário com o valor (faixa por idade); valorMensalidade null = sem tabela.
  getProdutosComPrecoBeneficiario(
    guidBeneficiario: string
  ): Observable<ProdutoComPrecoDoBeneficiario[]> {
    const url = `${endpoints.produtoBeneficiario}/precos/${guidBeneficiario}`;
    return this.http.get<ProdutoComPrecoDoBeneficiario[]>(url);
  }

  exportProdutosBeneficiario() {
    const baseUrl = endpoints.produtoBeneficiario;
    const url = `${baseUrl}/exportar`;

    return this.http.post(url, null, {
      responseType: 'arraybuffer',
    });
  }

  addProdutoBeneficiario(
    payload: CreateProdutoBeneficiarioPayload
  ): Observable<void> {
    const baseUrl = endpoints.produtoBeneficiario;

    return this.http.post<void>(baseUrl, payload);
  }

  bloquearProdutoBeneficiario(
    payload: BloquearProdutoBeneficiarioPayload
  ): Observable<void> {
    const baseUrl = endpoints.produtoBeneficiario;
    const url = `${baseUrl}/bloqueio`;

    return this.http.put<void>(url, payload);
  }

  alterarCarteirinhaProdutoBeneficiario(payload: {
    guidProdutoBeneficiario: string;
    carteirinha: string;
  }): Observable<void> {
    const baseUrl = endpoints.produtoBeneficiario;
    const url = `${baseUrl}/carteirinha/${payload.carteirinha}/guid/${payload.guidProdutoBeneficiario}`;

    return this.http.put<void>(url, {});
  }

  getDocumentosBeneficiario(
    guidBeneficiario: string
  ): Observable<DocumentoBeneficiario[]> {
    const baseUrl = endpoints.documentoBeneficiario;
    const url = `${baseUrl}/${guidBeneficiario}`;

    return this.http.get<DocumentoBeneficiario[]>(url);
  }

  addDocumentoBeneficiario(
    guidBeneficiario: string,
    guidDocumento: string,
    documento: File
  ): Observable<DocumentoBeneficiario> {
    const url = endpoints.documentoBeneficiario;
    const formData = new FormData();

    formData.append('guidBeneficiario', guidBeneficiario);
    formData.append('guidDocumento', guidDocumento);
    formData.append('documento', documento);

    return this.http.post<DocumentoBeneficiario>(url, formData);
  }

  deleteDocumentosBeneficiario(
    guidBeneficiario: string,
    guidDocumento: string
  ): Observable<void> {
    const url = endpoints.documentoBeneficiario;
    const payload = {
      guidBeneficiario,
      guidDocumento,
    };

    return this.http.delete<void>(url, {
      body: payload,
    });
  }

  getProdutoBeneficiarioPainelDeAtendimento(
    filtroCPFBeneficiario?: string
  ): Observable<ProdutoBeneficiarioPainelAtendimento[]> {
    const baseUrl = endpoints.produtoBeneficiario;
    const url = `${baseUrl}/painel-de-atendimento`;

    let params = new HttpParams();

    if (!isNullOrWhiteSpace(filtroCPFBeneficiario)) {
      params = params.set('filtroCPFBeneficiario', `${filtroCPFBeneficiario}`);
    }

    return this.http.get<ProdutoBeneficiarioPainelAtendimento[]>(url, {
      params,
    });
  }
}
